import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { X, ChevronRight, Plus, Trash2, Building2, Loader2, RotateCcw } from 'lucide-react';
import { getFacilities, createJobByCustomerCompanyManager } from '../../../../services/staffingService';
import QuickCreateFacilityPopover from './QuickCreateFacilityPopover';
import toast from 'react-hot-toast';

const SERVICE_TYPES = ['Standing', 'Event', 'Patrol', 'Storefront', 'Workplace', 'Traffic'];

const DAYS = [
  { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 }, { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 }, { label: 'Sun', value: 0 },
];

const MAX_RECURRING = 90;

// US state abbreviation → IANA timezone (dominant zone per state)
const STATE_TO_TZ = {
  AL:'America/Chicago',  AK:'America/Anchorage', AZ:'America/Phoenix',
  AR:'America/Chicago',  CA:'America/Los_Angeles',CO:'America/Denver',
  CT:'America/New_York', DE:'America/New_York',  FL:'America/New_York',
  GA:'America/New_York', HI:'Pacific/Honolulu',  ID:'America/Boise',
  IL:'America/Chicago',  IN:'America/Indiana/Indianapolis', IA:'America/Chicago',
  KS:'America/Chicago',  KY:'America/New_York',  LA:'America/Chicago',
  ME:'America/New_York', MD:'America/New_York',  MA:'America/New_York',
  MI:'America/Detroit',  MN:'America/Chicago',   MS:'America/Chicago',
  MO:'America/Chicago',  MT:'America/Denver',    NE:'America/Chicago',
  NV:'America/Los_Angeles', NH:'America/New_York', NJ:'America/New_York',
  NM:'America/Denver',   NY:'America/New_York',  NC:'America/New_York',
  ND:'America/Chicago',  OH:'America/New_York',  OK:'America/Chicago',
  OR:'America/Los_Angeles', PA:'America/New_York', RI:'America/New_York',
  SC:'America/New_York', SD:'America/Chicago',   TN:'America/Chicago',
  TX:'America/Chicago',  UT:'America/Denver',    VT:'America/New_York',
  VA:'America/New_York', WA:'America/Los_Angeles',WV:'America/New_York',
  WI:'America/Chicago',  WY:'America/Denver',    DC:'America/New_York',
};

// Local date string helpers (avoids .toISOString() UTC shift for UTC+ users)
function localDateStr(daysAhead = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

// 2-day minimum buffer — mirrors the officer-landing ShiftPicker minDate pattern
const MIN_DATE = localDateStr(2);

function emptyShift() {
  return { date: MIN_DATE, startTime: '08:00', endTime: '16:00', staffsRequired: 1 };
}

function expandRecurring({ start, end, days, startTime, endTime, staffsRequired }) {
  if (!start || !end || !days.length || !startTime || !endTime) return [];
  const result = [];
  const cur = new Date(start + 'T00:00:00');
  const last = new Date(end + 'T00:00:00');
  while (cur <= last && result.length < MAX_RECURRING) {
    if (days.includes(cur.getDay())) {
      // Use local date parts — .toISOString() would shift UTC+ users back one day
      const d = [cur.getFullYear(), String(cur.getMonth() + 1).padStart(2, '0'), String(cur.getDate()).padStart(2, '0')].join('-');
      result.push({ date: d, startTime, endTime, staffsRequired: Number(staffsRequired) });
    }
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

const STEPS = ['Details', 'Shifts', 'Confirm'];

// ── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                  done    ? 'bg-emerald-500 text-white' :
                  active  ? 'bg-[#404880] text-white' :
                            'bg-gray-100 text-gray-400'
                }`}
              >
                {done ? '✓' : i + 1}
              </div>
              <span className={`text-xs font-medium ${active ? 'text-gray-900' : done ? 'text-emerald-600' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px w-6 flex-shrink-0 ${i < current ? 'bg-emerald-300' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * prefillData — optional, used for "Repeat Order".
 * Shape: { serviceType, description, hourlyPayRate, facilityId, shiftTemplates: [{ startTime, endTime, staffsRequired }] }
 * Shift templates have times pre-filled but NO dates — user must supply new dates.
 */
const NewWorkOrderSlideOver = ({ open, onClose, initialFacilityId = null, onCreated, prefillData = null }) => {
  const { customerCompanyId } = useParams();
  const overlayRef = useRef(null);

  const [step, setStep]             = useState(0);
  const [facilities, setFacilities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [quickCreate, setQuickCreate] = useState(false);

  // Step 0 fields
  const [form, setForm] = useState({
    serviceType: '',
    description: '',
    facilityId: initialFacilityId || '',
    hourlyPayRate: '',
  });

  // Step 1 — manual shifts
  const [shifts, setShifts] = useState([emptyShift()]);

  // Step 1 — recurring schedule
  const [shiftMode, setShiftMode] = useState('manual');
  const [recurring, setRecurring] = useState({
    start: '', end: '', days: [1], startTime: '08:00', endTime: '16:00', staffsRequired: 1,
  });
  const recurringShifts = useMemo(() => expandRecurring(recurring), [recurring]);
  const activeShifts    = shiftMode === 'recurring' ? recurringShifts : shifts;

  // Sync initialFacilityId when it changes
  useEffect(() => {
    if (initialFacilityId) setForm(f => ({ ...f, facilityId: initialFacilityId }));
  }, [initialFacilityId]);

  // Load facilities on open
  useEffect(() => {
    if (!open) return;
    getFacilities(customerCompanyId)
      .then(r => setFacilities(r.data || []))
      .catch(() => {});
  }, [open, customerCompanyId]);

  // Reset when closed; pre-fill when opened in "repeat order" mode
  useEffect(() => {
    if (open && prefillData) {
      // Opening as a repeat — seed all fields from the previous job
      setStep(0);
      setForm({
        serviceType:   prefillData.serviceType   || '',
        description:   prefillData.description   || '',
        facilityId:    prefillData.facilityId    || initialFacilityId || '',
        hourlyPayRate: prefillData.hourlyPayRate != null ? String(prefillData.hourlyPayRate) : '',
      });
      const templates = prefillData.shiftTemplates || [];
      setShifts(
        templates.length > 0
          ? templates.map(t => ({
              date:           '',              // user must supply new dates
              startTime:      t.startTime      || '08:00',
              endTime:        t.endTime        || '16:00',
              staffsRequired: t.staffsRequired || 1,
            }))
          : [emptyShift()]
      );
      setShiftMode('manual');
      setRecurring({ start: '', end: '', days: [1], startTime: '08:00', endTime: '16:00', staffsRequired: 1 });
    } else if (!open) {
      setStep(0);
      setForm({ serviceType: '', description: '', facilityId: initialFacilityId || '', hourlyPayRate: '' });
      setShifts([emptyShift()]);
      setShiftMode('manual');
      setRecurring({ start: '', end: '', days: [1], startTime: '08:00', endTime: '16:00', staffsRequired: 1 });
    }
  }, [open, initialFacilityId, prefillData]);

  const updateField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Shift helpers ──
  const addShift    = () => setShifts(s => [...s, emptyShift()]);
  const removeShift = (i) => setShifts(s => s.filter((_, idx) => idx !== i));
  const updateShift = (i, key, val) => setShifts(s => s.map((sh, idx) => idx === i ? { ...sh, [key]: val } : sh));

  // ── Validation ──
  const step0Valid = form.serviceType.trim() !== '' && form.facilityId !== '';
  const step1Valid = activeShifts.length > 0 && activeShifts.every(s =>
    s.date && s.startTime && s.endTime && Number(s.staffsRequired) >= 1 && s.date >= MIN_DATE
  );

  // ── Submit ──
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const facility = facilities.find(f => f._id === form.facilityId);
      const formattedAddress = facility
        ? [facility.address, facility.city, facility.state, facility.zipcode].filter(Boolean).join(', ')
        : '';
      const totalStaff = activeShifts.reduce((sum, s) => sum + Number(s.staffsRequired), 0);

      const jobData = {
        serviceType:       form.serviceType,
        service:           form.serviceType,
        memo:              form.description,
        hourlyPayRate:     Number(form.hourlyPayRate) || 0,
        facilityId:        form.facilityId || undefined,
        location:          formattedAddress,
        formatted_address: formattedAddress,
        state:             facility?.state   || '',
        city:              facility?.city    || '',
        zipCode:           facility?.zipcode || '',
        latitude:          facility?.geoLocation?.lat || 0,
        longitude:         facility?.geoLocation?.lng || 0,
        // No browser timezone — server derives tz from the facility's state
        // so a dispatcher in Berlin booking a Lexington MA facility gets
        // Eastern time, not Central European time.
        staffsQty: totalStaff,
        shifts: activeShifts.map(s => ({
          date:           s.date,
          // Naive datetime strings (no Z / offset) — server interprets them
          // in the facility's IANA timezone using Luxon.
          startTime:      `${s.date}T${s.startTime}:00`,
          endTime:        `${s.date}T${s.endTime}:00`,
          staffsRequired: Number(s.staffsRequired),
        })),
      };

      // staffingService.js wraps as { jobData: job }, so pass jobData directly
      await createJobByCustomerCompanyManager(customerCompanyId, jobData);
      toast.success('Work order created successfully!');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm pointer-events-auto"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-[520px] flex-col bg-white shadow-2xl pointer-events-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {prefillData ? 'Repeat Work Order' : 'New Work Order'}
            </h2>
            {prefillData ? (
              <p className="text-xs text-[#CC457D] mt-0.5 flex items-center gap-1">
                <RotateCcw size={11} /> Pre-filled from previous {prefillData.serviceType} order
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">Security service request</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="border-b border-gray-100 px-6 py-3">
          <StepIndicator current={step} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── Step 0: Details ─────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-5">
              {prefillData && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <RotateCcw size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Details copied from your previous order — review each field and adjust as needed before proceeding.
                  </p>
                </div>
              )}
              {/* Service type */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">
                  Service Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.serviceType}
                  onChange={e => updateField('serviceType', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]"
                >
                  <option value="">Select service type…</option>
                  {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={e => updateField('description', e.target.value)}
                  placeholder="Any specific instructions, access notes, or requirements…"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880] resize-none"
                />
              </div>

              {/* Hourly Rate */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">
                  Hourly Rate <span className="text-gray-400 font-normal">($/hr, optional)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 35"
                  value={form.hourlyPayRate}
                  onChange={e => updateField('hourlyPayRate', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]"
                />
              </div>

              {/* Facility (required) */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">
                  Facility <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.facilityId}
                  onChange={e => updateField('facilityId', e.target.value)}
                  className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 ${
                    form.facilityId === '' && form.serviceType ? 'border-red-300 focus:border-red-400 focus:ring-red-300' : 'border-gray-200 focus:border-[#404880] focus:ring-[#404880]'
                  }`}
                >
                  <option value="">Select a facility…</option>
                  {facilities.map(f => (
                    <option key={f._id} value={f._id}>
                      {f.name}{f.city ? ` — ${f.city}` : ''}
                    </option>
                  ))}
                </select>

                {form.facilityId === '' && form.serviceType ? (
                  <p className="mt-1 text-[11px] text-red-500">A facility is required to create a work order</p>
                ) : null}

                {!quickCreate && (
                  <button
                    type="button"
                    onClick={() => setQuickCreate(true)}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-[#404880] transition-colors"
                  >
                    <Plus size={10} /> Add new facility
                  </button>
                )}
                {quickCreate && (
                  <QuickCreateFacilityPopover
                    customerCompanyId={customerCompanyId}
                    onCreated={(newFacility) => {
                      setFacilities(prev => [...prev, newFacility]);
                      updateField('facilityId', newFacility._id);
                      setQuickCreate(false);
                    }}
                    onCancel={() => setQuickCreate(false)}
                  />
                )}

                {/* Timezone note — shown once a facility is selected */}
                {form.facilityId && (() => {
                  const fac = facilities.find(f => f._id === form.facilityId);
                  const tz  = fac?.state ? (STATE_TO_TZ[fac.state] || 'America/New_York') : null;
                  return tz ? (
                    <p className="mt-2 text-[11px] text-gray-400">
                      Shift times use the facility’s timezone:{' '}
                      <span className="font-medium text-gray-500">{tz}</span>
                    </p>
                  ) : null;
                })()} 
              </div>

              {/* Security agency selection — disabled for now */}

              {/* Date range removed — shifts define actual work dates/times */}
            </div>
          )}

          {/* ── Step 1: Shifts ──────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              {prefillData && shiftMode === 'manual' && shifts.some(s => !s.date) && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <RotateCcw size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Shift times and officer counts copied from the original — <strong>select a new date</strong> for each shift.
                  </p>
                </div>
              )}
              {/* Mode toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button onClick={() => setShiftMode('manual')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${shiftMode === 'manual' ? 'bg-[#404880] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                  Manual shifts
                </button>
                <button onClick={() => setShiftMode('recurring')}
                  className={`flex-1 py-2 text-xs font-medium transition-colors ${shiftMode === 'recurring' ? 'bg-[#404880] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                  ↻ Recurring schedule
                </button>
              </div>

              {/* ── Manual mode ── */}
              {shiftMode === 'manual' && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-500">Add one or more shifts</p>
                    <button onClick={addShift} className="flex items-center gap-1 rounded-lg border border-dashed border-[#404880] px-3 py-1.5 text-xs font-medium text-[#404880] hover:bg-[#404880]/5 transition-colors">
                      <Plus size={12} /> Add shift
                    </button>
                  </div>
                  {shifts.map((sh, i) => (
                    <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">Shift {i + 1}</span>
                        {shifts.length > 1 && (
                          <button onClick={() => removeShift(i)} className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="mb-1 block text-[11px] font-medium text-gray-600">Date</label>
                          <input type="date" value={sh.date} min={MIN_DATE} onChange={e => updateShift(i, 'date', e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-medium text-gray-600">Start Time</label>
                          <input type="time" value={sh.startTime} onChange={e => updateShift(i, 'startTime', e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] font-medium text-gray-600">End Time</label>
                          <input type="time" value={sh.endTime} onChange={e => updateShift(i, 'endTime', e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                        </div>
                        <div className="col-span-2">
                          <label className="mb-1 block text-[11px] font-medium text-gray-600">Officers Required</label>
                          <input type="number" min="1" max="100" value={sh.staffsRequired} onChange={e => updateShift(i, 'staffsRequired', e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ── Recurring mode ── */}
              {shiftMode === 'recurring' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-gray-600">From</label>
                      <input type="date" value={recurring.start} min={MIN_DATE} onChange={e => setRecurring(r => ({ ...r, start: e.target.value }))} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-gray-600">To</label>
                      <input type="date" value={recurring.end} min={MIN_DATE} onChange={e => setRecurring(r => ({ ...r, end: e.target.value }))} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] font-medium text-gray-600">Days of week</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {DAYS.map(d => (
                        <button key={d.value} type="button"
                          onClick={() => setRecurring(r => ({ ...r, days: r.days.includes(d.value) ? r.days.filter(x => x !== d.value) : [...r.days, d.value] }))}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${recurring.days.includes(d.value) ? 'bg-[#404880] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-gray-600">Start Time</label>
                      <input type="time" value={recurring.startTime} onChange={e => setRecurring(r => ({ ...r, startTime: e.target.value }))} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-gray-600">End Time</label>
                      <input type="time" value={recurring.endTime} onChange={e => setRecurring(r => ({ ...r, endTime: e.target.value }))} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                    </div>
                    <div className="col-span-2">
                      <label className="mb-1 block text-[11px] font-medium text-gray-600">Officers per shift</label>
                      <input type="number" min="1" max="100" value={recurring.staffsRequired} onChange={e => setRecurring(r => ({ ...r, staffsRequired: e.target.value }))} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]" />
                    </div>
                  </div>

                  {/* Live preview */}
                  {recurringShifts.length > 0 ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-xs font-semibold text-emerald-800 mb-2">
                        {recurringShifts.length} shift{recurringShifts.length !== 1 ? 's' : ''} will be created
                        {recurringShifts.length === MAX_RECURRING && <span className="ml-1 text-amber-700"> (max {MAX_RECURRING} reached)</span>}
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {recurringShifts.map((s, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px] text-emerald-700">
                            <span className="font-medium w-24">{s.date}</span>
                            <span>{s.startTime} – {s.endTime}</span>
                            <span>{s.staffsRequired} officer{s.staffsRequired > 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : recurring.start && recurring.end ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-[11px] text-amber-700">No matching dates found. Check the date range and selected days.</p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <p className="text-[11px] text-gray-400">Fill in the date range and days above to preview the schedule.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Confirm ─────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Summary</h3>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Service Type</p>
                    <p className="font-medium text-gray-900">{form.serviceType}</p>
                  </div>
                  {form.facilityId && (
                    <div>
                      <p className="text-xs text-gray-400">Facility</p>
                      <p className="font-medium text-gray-900 flex items-center gap-1">
                        <Building2 size={12} className="text-gray-400" />
                        {facilities.find(f => f._id === form.facilityId)?.name || form.facilityId}
                      </p>
                    </div>
                  )}
                  {form.description && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400">Description</p>
                      <p className="text-gray-700">{form.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400">Shifts</p>
                    <p className="font-medium text-gray-900">{activeShifts.length} shift{activeShifts.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Officers</p>
                    <p className="font-medium text-gray-900">
                      {activeShifts.reduce((sum, s) => sum + Number(s.staffsRequired), 0)} needed
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <p className="text-xs font-medium text-gray-500">Shift Schedule</p>
                  {activeShifts.map((sh, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white border border-gray-100 px-3 py-2">
                      <span className="text-xs text-gray-700 font-medium">{sh.date}</span>
                      <span className="text-xs text-gray-500">{sh.startTime} – {sh.endTime}</span>
                      <span className="text-xs font-semibold text-[#404880]">{sh.staffsRequired} officer{sh.staffsRequired > 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Submitting will create the work order and make it visible to security agencies.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {step === 0 ? 'Cancel' : '← Back'}
          </button>

          {step < 2 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 0 ? !step0Valid : !step1Valid}
              className="flex items-center gap-1.5 rounded-lg bg-[#404880] px-5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-brand-pink px-5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : 'Submit Work Order'}
            </button>
          )}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default NewWorkOrderSlideOver;
